import {
  FreshStrawTransferredEmbryo,
  useGetFreshStrawTransferredEmbryosQuery,
} from '@/app/services/freshStrawRest';

import type { FreshStrawSession } from '../../hooks/useFreshStrawSession';

interface FreshStrawTransferredTabProps {
  session: FreshStrawSession;
}

export function FreshStrawTransferredTab({
  session,
}: FreshStrawTransferredTabProps) {
  const stopNumber = Number(session.stopId);
  const ready =
    !!session.accountId &&
    !!session.transferSite &&
    !!session.transferDate &&
    Number.isFinite(stopNumber) &&
    stopNumber > 0;

  const query = useGetFreshStrawTransferredEmbryosQuery(
    {
      accountId: session.accountId,
      siteId: session.transferSite,
      stopNumber,
      implantDate: session.transferDate,
    },
    { skip: !ready },
  );

  const rows = (query.data ?? []) as FreshStrawTransferredEmbryo[];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {!ready && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Set account, transfer site, transfer date, and a numeric stop id in
          the filter panel to load transferred embryos for this trip stop.
        </div>
      )}

      {ready && query.isFetching && (
        <div className="text-sm text-gray-500">Loading…</div>
      )}

      {query.isError && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Failed to load transferred embryos.
        </div>
      )}

      {ready && !query.isFetching && rows.length === 0 && (
        <div className="flex flex-1 items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-500">
          No embryos have been transferred at this stop yet.
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="text-xs text-gray-600">
            Total transferred: {rows.length}
          </div>
          <div className="min-h-0 flex-1 overflow-auto rounded border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-100 text-left text-xs font-semibold text-gray-600">
                <tr>
                  <th className="px-2 py-1">Mating</th>
                  <th className="px-2 py-1">Sex</th>
                  <th className="px-2 py-1">Ranch</th>
                  <th className="px-2 py-1">Recip</th>
                  <th className="px-2 py-1">Cane</th>
                  <th className="px-2 py-1">Straw</th>
                  <th className="px-2 py-1">Tag</th>
                  <th className="px-2 py-1">Stage</th>
                  <th className="px-2 py-1">Grade</th>
                  <th className="px-2 py-1">Qual</th>
                  <th className="px-2 py-1">Dist</th>
                  <th className="px-2 py-1">CL</th>
                  <th className="px-2 py-1">Asp</th>
                  <th className="px-2 py-1">Comment</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={`${r.embryoId ?? 'row'}-${i}`}
                    className="border-t border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-2 py-1">{r.mating ?? ''}</td>
                    <td className="px-2 py-1">{r.sex ?? ''}</td>
                    <td className="px-2 py-1">{r.ranchCode ?? ''}</td>
                    <td className="px-2 py-1">{r.recipName ?? ''}</td>
                    <td className="px-2 py-1">{r.cane ?? ''}</td>
                    <td className="px-2 py-1">{r.strawNumber ?? ''}</td>
                    <td className="px-2 py-1">{r.tagNumber ?? ''}</td>
                    <td className="px-2 py-1">{r.stage ?? ''}</td>
                    <td className="px-2 py-1">{r.grade ?? ''}</td>
                    <td className="px-2 py-1">{r.quality ?? ''}</td>
                    <td className="px-2 py-1">{r.distance ?? ''}</td>
                    <td className="px-2 py-1">{r.clValue ?? ''}</td>
                    <td className="px-2 py-1">
                      {[r.aspDate, r.aspSite].filter(Boolean).join(' / ')}
                    </td>
                    <td className="px-2 py-1">{r.implantComment ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
